import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { getCategories } from '../services/productService';
import './styles/Layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cartItemCount } = useCart();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [productsExpanded, setProductsExpanded] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Get current category and search from URL
  const category = searchParams.get('category');
  const search = searchParams.get('search');

  const isActive = (path: string) => location.pathname === path;

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const cats = await getCategories();
        setCategories(cats);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };

    fetchCategories();
  }, []);

  // Auto-expand categories when viewing a category
  useEffect(() => {
    if (category) {
      setProductsExpanded(true);
    }
  }, [category]);

  // Blur search input when sidebar closes (to remove focus/outline)
  useEffect(() => {
    if (!sidebarOpen) {
      // Remove focus from search input when sidebar closes
      const searchInput = document.querySelector('.search-input') as HTMLInputElement;
      if (searchInput) {
        searchInput.blur();
      }
    }
  }, [sidebarOpen]);

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchTerm.trim();
    
    // Client-side validation
    if (!trimmed) {
      return;
    }
    
    if (trimmed.length < 2) {
      // Could add error state here
      return;
    }
    
    if (trimmed.length > 100) {
      // Could add error state here
      setSearchTerm(trimmed.substring(0, 100));
      return;
    }
    
    navigate(`/products?search=${encodeURIComponent(trimmed)}`);
    setSidebarOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Prevent ultra-long input
    if (value.length <= 100) {
      setSearchTerm(value);
    }
  };

  return (
    <div className="layout">
      {/* Left Sidebar Navigation */}
      <aside 
        className={`sidebar ${sidebarOpen ? 'open' : ''}`}
        onMouseEnter={() => {
          // Only auto-open on desktop (hover)
          if (window.innerWidth >= 1024) {
            setSidebarOpen(true);
          }
        }}
        onMouseLeave={() => {
          // Only auto-close on desktop (hover)
          if (window.innerWidth >= 1024) {
            setSidebarOpen(false);
            setProductsExpanded(false); // Auto-collapse categories
          }
        }}
      >
        {/* Logo */}
        <Link to="/" className="sidebar-logo" onClick={() => setSidebarOpen(false)}>
          <svg className="logo-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L9 9L2 9L7.5 13L5 20L12 15L19 20L16.5 13L22 9L15 9L12 2Z" />
          </svg>
          <span className="logo-text">Maple Nook</span>
        </Link>

        {/* Main Navigation */}
        <nav className="sidebar-nav">
          {/* Home */}
          <Link 
            to="/" 
            className={`nav-item ${isActive('/') && !search && !category ? 'active' : ''}`}
            onClick={() => {
              setSidebarOpen(false);
              setProductsExpanded(false);
            }}
          >
            <svg className="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span className="nav-text">Home</span>
          </Link>

          {/* Categories (expandable) */}
          <div className="nav-section">
            <button
              className={`nav-item nav-expandable ${location.pathname === '/products' && category ? 'active' : ''} ${productsExpanded ? 'expanded' : ''}`}
              onClick={() => setProductsExpanded(!productsExpanded)}
            >
              <svg className="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              <span className="nav-text">Products</span>
              <svg className="expand-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Category Submenu */}
            <div className={`nav-submenu ${productsExpanded ? 'expanded' : ''}`}>
              {categories.map((cat) => (
                <Link 
                  key={cat}
                  to={`/products?category=${encodeURIComponent(cat)}`} 
                  className={`nav-subitem ${location.pathname === '/products' && category === cat ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="subitem-dot"></span>
                  <span className="nav-text">{cat}</span>
                </Link>
              ))}
              {categories.length === 0 && (
                <div className="nav-subitem nav-loading">
                  <span className="subitem-dot"></span>
                  <span className="nav-text">Loading...</span>
                </div>
              )}
            </div>
          </div>

          {/* Search */}
          <form className={`nav-item nav-search ${location.pathname === '/products' && search ? 'active' : ''}`} onSubmit={handleSearch}>
            <svg className="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input 
              type="text" 
              className="search-input" 
              placeholder="Search products..."
              value={searchTerm}
              onChange={handleSearchChange}
              maxLength={100}
              minLength={2}
              disabled={!sidebarOpen}
            />
          </form>

          {/* Spacer to push bottom items down */}
          <div className="nav-spacer"></div>

          {/* Bottom Navigation Items */}
          <div className="nav-bottom">
            <Link 
              to="/about" 
              className={`nav-item ${isActive('/about') ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <svg className="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="nav-text">About Us</span>
            </Link>

            <Link 
              to="/contact" 
              className={`nav-item ${isActive('/contact') ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <svg className="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="nav-text">Contact</span>
            </Link>

            <Link 
              to="/cart" 
              className={`nav-item ${isActive('/cart') ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <div className="cart-icon-wrapper">
                <svg className="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {cartItemCount > 0 && (
                  <span className="nav-cart-badge">{cartItemCount}</span>
                )}
              </div>
              <span className="nav-text">Cart</span>
            </Link>
          </div>
        </nav>

        {/* Footer in Sidebar */}
        <div className="sidebar-footer">
          <p className="sidebar-footer-text">© 2026 Maple Nook</p>
        </div>
      </aside>

      {/* Mobile Toggle Button */}
      <button 
        className="sidebar-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle navigation"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

export default Layout;